/**
 * campaignIdentity - the three names a campaign carries, and which one each
 * admin label means (R10, fund identity, 2026-10).
 *
 *   Campaign Name      the offering's title as investors see it. For a FUND that
 *                      is fund_name, falling back to campaign_name: the exact
 *                      rule the portal's fund pages use (FundCard, FundDetails,
 *                      FundInvest*: `fund?.fund_name || fund?.campaign_name`).
 *                      For EQUITY and LENDME it is campaign_name.
 *   Issuer legal name  corporation_legal_name: the legal entity that issues the
 *                      securities and is named in the listing agreement.
 *   Fund manager       fund_manager_name (FUND only).
 *
 * THE DEFECT. The campaign queues (every status but Active), the investment
 * view/edit exports and the corporate-account branch of those pages labelled
 * corporation_legal_name "Campaign Name". For a fund the legal issuer and the
 * series are different strings, so an approver saw the wrong one under the
 * label they relied on, and nothing errored.
 *
 * THE RULE THAT MUST HOLD: the Campaign Name fallback chain NEVER ends at
 * corporation_legal_name. Borrowing the legal name when the series name is
 * blank would recreate the defect for exactly the rows that need attention;
 * absence is rendered as UNSET instead, as FundDetailFields does.
 *
 * Lives under pages/Campaign (not utils/) because the investment pages are the
 * only other consumers and the R10 ownership split keeps this change inside
 * src/pages/Campaign and src/pages/Investment.
 */

export const CAMPAIGN_NAME_LABEL = "Campaign Name";
export const ISSUER_LEGAL_NAME_LABEL = "Issuer legal name";
export const FUND_MANAGER_LABEL = "Fund manager";
// W3b: the other columns a claimed listing agreement binds. The API locks them
// for the issuer once the agreement is claimed, so this correction covers them.
export const REGISTERED_ADDRESS_LABEL = "Registered address";
export const NOTICE_EMAIL_LABEL = "Notice e-mail";
export const ISSUER_JURISDICTION_LABEL = "Issuer jurisdiction";
export const UNSET = "— not set";

/**
 * Who the UI offers the issuer-identity edit to. Mirrors the API's
 * requireRole('AD', 'CCO') on PUT /admin/campaign/:id/issuer-identity, which
 * has NO inheritance: UDP does not pass a CCO gate there, so it is not listed
 * here either. The server is the control; this only hides a button that would
 * 403.
 */
export const ISSUER_IDENTITY_EDIT_ROLES = ["AD", "CCO"];

/** The API refuses a reason shorter than this (after trimming). */
export const MIN_REASON_LENGTH = 20;

const clean = (v) => (v === null || v === undefined ? "" : String(v).trim());

/**
 * Same idiom as CampaignViewPage / CampaignSchedulePanel: `type` reaches the
 * column unnormalised, so a lowercase "fund" must still be a fund.
 */
export const isFundCampaign = (campaign) =>
  String(campaign?.type || "").trim().toUpperCase() === "FUND";

/**
 * The offering's title as investors see it; "" when it has none. The FIELD is
 * chosen exactly as the portal chooses it (raw `fund_name || campaign_name`)
 * and only then trimmed, so a whitespace-only fund_name - a blank title on the
 * investor pages - reads as unset here instead of being covered up.
 */
export function campaignDisplayName(campaign) {
  if (!campaign) return "";
  if (isFundCampaign(campaign)) return clean(campaign.fund_name || campaign.campaign_name);
  return clean(campaign.campaign_name);
}

/** Display form of a possibly-absent value. */
export const orUnset = (v) => clean(v) || UNSET;

/**
 * The two name columns of the campaign queues, for every status. Replaces the
 * per-status branch that put corporation_legal_name under "Campaign Name".
 * The field keys stay campaign_name / corporation_legal_name so the responsive
 * column priorities keyed on them keep applying.
 */
export function campaignNameColumns() {
  return [
    {
      field: "campaign_name",
      headerName: CAMPAIGN_NAME_LABEL,
      flex: 1,
      minWidth: 170,
      valueGetter: (params) => orUnset(campaignDisplayName(params?.row)),
    },
    {
      field: "corporation_legal_name",
      headerName: ISSUER_LEGAL_NAME_LABEL,
      flex: 1,
      minWidth: 170,
      valueGetter: (params) => orUnset(params?.row?.corporation_legal_name),
    },
  ];
}

/**
 * The two identity cells of the investment Excel exports, in sheet order.
 * Empty rather than UNSET: a spreadsheet cell is data, not a review screen.
 */
export function identityExportColumns(campaignDetail) {
  return {
    [CAMPAIGN_NAME_LABEL]: campaignDisplayName(campaignDetail),
    [ISSUER_LEGAL_NAME_LABEL]: clean(campaignDetail?.corporation_legal_name),
  };
}

export const issuerIdentityUrl = (apiBase, campaignId) =>
  `${apiBase}admin/campaign/${campaignId}/issuer-identity`;

/**
 * Client-side refusal, or null when the edit may be sent. The server repeats
 * every check; these exist so an obviously invalid edit never becomes a
 * request, and so a no-op never writes an empty before/after audit row.
 */
export function validateIssuerIdentityEdit({
  campaign, legalName, managerName, reason, registeredAddress, noticeEmail, issuerJurisdiction,
}) {
  const fund = isFundCampaign(campaign);
  const nextLegal = clean(legalName);
  // The API's wording (raiseCapital.controller.js issuer-identity refusal).
  if (!nextLegal) return "The issuer legal name can be corrected but not removed. Enter the legal name.";
  if (clean(reason).length < MIN_REASON_LENGTH) {
    return `Give a reason of at least ${MIN_REASON_LENGTH} characters; it is recorded in the audit log.`;
  }
  // W3b: a stored bound column can be corrected, never blanked (the API refuses it too).
  for (const [field, value, label] of boundFieldInputs({
    campaign, registeredAddress, noticeEmail, issuerJurisdiction,
  })) {
    if (value === undefined) continue;
    if (!clean(value) && clean(campaign?.[field])) {
      return `The ${label.toLowerCase()} can be corrected but not removed.`;
    }
    if (field === "camp_email" && clean(value) && !EMAIL_RE.test(clean(value))) {
      return "The notice e-mail must be an e-mail address.";
    }
  }
  const legalChanged = nextLegal !== clean(campaign?.corporation_legal_name);
  const managerChanged = fund && clean(managerName) !== clean(campaign?.fund_manager_name);
  const boundChanged = Object.keys(
    changedBoundFields({ campaign, registeredAddress, noticeEmail, issuerJurisdiction })
  ).length > 0;
  if (!legalChanged && !managerChanged && !boundChanged) {
    return fund
      ? "Nothing to save: the legal name and the fund manager are unchanged."
      : "Nothing to save: the legal name is unchanged.";
  }
  return null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** [column, edited value (undefined = not offered), label] for the W3b bound columns. */
function boundFieldInputs({ campaign, registeredAddress, noticeEmail, issuerJurisdiction }) {
  const rows = [
    ["registered_address", registeredAddress, REGISTERED_ADDRESS_LABEL],
    ["camp_email", noticeEmail, NOTICE_EMAIL_LABEL],
  ];
  if (isFundCampaign(campaign)) rows.push(["issuer_jurisdiction", issuerJurisdiction, ISSUER_JURISDICTION_LABEL]);
  return rows;
}

/** The W3b bound columns the edit changes (non-blank and different from the stored value). */
function changedBoundFields(input) {
  const out = {};
  for (const [field, value] of boundFieldInputs(input)) {
    if (value === undefined) continue;
    const next = clean(value);
    if (next && next !== clean(input.campaign?.[field])) out[field] = next;
  }
  return out;
}

/**
 * The request body. fund_manager_name is sent for a FUND only (the API refuses
 * it on other types), and a blank manager is null rather than "". W3b: a bound
 * column (registered address, notice e-mail, a fund's issuer jurisdiction) is
 * sent only when it changed, so an untouched form sends exactly what it did.
 */
export function buildIssuerIdentityPayload({
  campaign, legalName, managerName, reason, registeredAddress, noticeEmail, issuerJurisdiction,
}) {
  const body = { corporation_legal_name: clean(legalName) };
  if (isFundCampaign(campaign)) body.fund_manager_name = clean(managerName) || null;
  Object.assign(body, changedBoundFields({ campaign, registeredAddress, noticeEmail, issuerJurisdiction }));
  body.reason = clean(reason);
  return body;
}
